"use client";

import type React from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { env } from "@/env";
import { getStoryblokApi } from "@/lib/storyblok";

interface ConfigContextType {
  // biome-ignore lint/suspicious/noExplicitAny: Storyblok types are complex
  config: any;
  isLoading: boolean;
  error: string | null;
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

export function ConfigProvider({ children }: { children: React.ReactNode }) {
  // biome-ignore lint/suspicious/noExplicitAny: Storyblok types are complex
  const [config, setConfig] = useState<any>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error] = useState<string | null>(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const storyblokApi = getStoryblokApi();
        const { data } = await storyblokApi.get("cdn/stories/config", {
          version:
            env.NEXT_PUBLIC_STORYBLOK_PREVIEW === "true"
              ? "published"
              : "draft",
          resolve_links: "url",
        });
        setConfig(data.story.content);
      } catch (error) {
        console.error("Error fetching Storyblok config:", error);
        setConfig({}); // Fallback to empty config
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfig();
  }, []);

  return (
    <ConfigContext.Provider value={{ config, isLoading, error }}>
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfig() {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error("useConfig must be used within a ConfigProvider");
  }
  return context;
}
